import { Box, keyframes } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useMemo } from 'react';

import { BodyCenter } from '../BodyCenter';

export enum SpinnerColors {
  Primary,
  Secondary,
  White,
}

const bounceKeyframes = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1.0);
  }
`;

const SpinnerRoot = styled('div')({
  textAlign: 'center',
  display: 'inline-block',
});

const Bounce = styled('div', {
  shouldForwardProp: prop => prop !== 'color' && prop !== 'delay',
})<{ color: SpinnerColors; delay?: number }>(({ theme, color, delay }) => ({
  opacity: 0.4,
  borderRadius: '100%',
  display: 'inline-block',
  animation: `${bounceKeyframes} 1.4s infinite ease-in-out both`,
  ...(delay !== undefined && { animationDelay: `${delay}s` }),
  ...(color === SpinnerColors.Primary && {
    backgroundColor: theme.palette.primary.main,
  }),
  ...(color === SpinnerColors.Secondary && {
    backgroundColor: theme.palette.secondary.main,
  }),
  ...(color === SpinnerColors.White && {
    backgroundColor: theme.palette.common.white,
  }),
}));

interface SpinnerProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'color'
> {
  color?: SpinnerColors;
  size?: number;
}

export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = 50, color = SpinnerColors.Primary, ...rest }, ref) => {
    const third = Math.floor(size / 3);
    const style = useMemo(() => {
      return {
        width: third,
        height: third,
      };
    }, [third]);
    return (
      <SpinnerRoot
        {...rest}
        data-test="spinner"
        ref={ref}
        style={{ width: size }}
      >
        <Bounce color={color} delay={-0.32} style={style} />
        <Bounce color={color} delay={-0.16} style={style} />
        <Bounce color={color} style={style} />
      </SpinnerRoot>
    );
  },
);

export const SpinnerCenter = React.forwardRef<HTMLDivElement, SpinnerProps>(
  (props, ref) => (
    <Box textAlign="center">
      <Spinner ref={ref} {...props} />
    </Box>
  ),
);

export const SpinnerGridCenter = React.forwardRef<HTMLDivElement, SpinnerProps>(
  (props, ref) => {
    return (
      <BodyCenter>
        <Spinner {...props} />
      </BodyCenter>
    );
  },
);
